import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { exportVisibleColumns } from "./tableExcel";
import { nextMobileSort, sortMobileRows, type MobileSortState } from "./mobileSorting";
import { storeOverviewCopy } from "./storeOverviewPresentation";
import { adviserOverviewColumns, type AdviserOverviewColumn, type ExistingAdviserColumn } from "./adviserOverviewPresentation";
import { desktopMetricHelp } from "./desktopInformation";
import { TableWindowSpacer, useTableWindow } from "./tableWindow";
import "./storeOverview.css";
import "./adviserOverview.css";
const percent = new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

type DisplayRow = Record<string, string | number | null>;
type Props = {
  rows: DisplayRow[];
  totalRow: DisplayRow;
  columns: ExistingAdviserColumn[];
  language: string;
  hiddenColumns: Set<string>;
  expectedTarget: number | null;
  ariaLabel: string;
  currentSort: () => MobileSortState;
  parseNumber: (value: unknown) => number | null;
  onViewChange: (sort: MobileSortState, rowIds: string[]) => void;
  children: ReactNode;
};

const mobileQuery = "(max-width: 900px), (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 600px)";

// Mount only one view and one export registry. Mobile keeps the established
// ProfessionalDataGrid summary/detail explorer supplied by the caller.
export default function AdviserOverviewTable(props: Props) {
  const [mobile, setMobile] = useState(() => window.matchMedia(mobileQuery).matches);
  useEffect(() => {
    const query = window.matchMedia(mobileQuery);
    const update = () => setMobile(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return mobile ? props.children : <DesktopAdviserOverview {...props} />;
}

function DesktopAdviserOverview(props: Props) {
  const columns = adviserOverviewColumns(props.columns, props.language).filter(column => !props.hiddenColumns.has(column.key));
  const [sort, setSort] = useState(props.currentSort);
  const referenceId = useId() + "-expected";
  const c = storeOverviewCopy(props.language);
  const columnSignature = JSON.stringify(props.columns.filter(column => !props.hiddenColumns.has(column.key)).map(column => column.key));
  useEffect(() => {
    // Screen-only daily metadata is never an export column. Keep the existing
    // caller's field keys/order and original Excel definitions unchanged.
    exportVisibleColumns.set(props.ariaLabel, JSON.parse(columnSignature));
    return () => { exportVisibleColumns.delete(props.ariaLabel); };
  }, [props.ariaLabel, columnSignature]);
  const rowId = (row: DisplayRow) => String(row.__row_id ?? `${row.store}\u001f${row.name}`);
  const sorted = useMemo(() => sortMobileRows(props.rows, sort, (row, key) => key === "store" || key === "name"
    ? String(row["__sort_" + key] ?? row[key] ?? "")
    : props.parseNumber(row["__sort_" + key] ?? row[key])), [props.rows, sort, props.parseNumber]);
  const rowSignature = useMemo(() => JSON.stringify(sorted.map(rowId)), [sorted]);
  const windowed = useTableWindow(sorted.length, rowSignature + JSON.stringify(sort));
  useEffect(() => {
    props.onViewChange(sort, sorted.map(rowId));
  }, [rowSignature, sort, props.onViewChange]);
  const width = columns.reduce((sum, column) => sum + column.width, 0);
  const groupEnd = (index: number) => index < columns.length - 1 && columns[index + 1].group !== columns[index].group;
  const achievementTone = (row: DisplayRow, key: string) => {
    if (key !== "achievement") return "";
    const actual = props.parseNumber(row.__sort_achievement ?? row.achievement);
    const expected = props.parseNumber(row.__expected_achievement);
    return actual == null || expected == null ? "" : actual >= expected ? "is-positive" : "is-negative";
  };
  const sortButton = (column: AdviserOverviewColumn) => <button type="button" data-sort-column={column.key}
    onClick={() => setSort(nextMobileSort(sort, column.key))} aria-label={column.label + " · " + c[25]} title={desktopMetricHelp(column.key, props.language)}>
    {sort?.key === column.key ? sort.direction === "asc" ? <ArrowUp size={12} aria-hidden /> : <ArrowDown size={12} aria-hidden /> : null}
    <span>{column.label}{column.key === "remaining" ? <><br />{c[5]}</> : null}</span>
  </button>;
  const cell = (row: DisplayRow, column: AdviserOverviewColumn) => {
    if (column.key === "remaining") return <span className="store-overview-remainder">
      <strong>{String(row.remaining ?? "—")}</strong>
      <span data-metric="dailyRequired" title={c[23]}>{String(row.__daily_required ?? "—")}</span>
    </span>;
    if (column.key === "store" || column.key === "name") return <span className="adviser-overview-identity-text" title={String(row[column.key] ?? "—")}>{String(row[column.key] ?? "—")}</span>;
    return String(row[column.key] ?? "—");
  };
  const renderRow = (row: DisplayRow, total: boolean, rowIndex?: number) => <tr aria-rowindex={rowIndex} key={total ? "__total" : rowId(row)} className={total ? "store-overview-total" : undefined}>
    {columns.map((column, index) => <td key={column.key} data-column={column.key}
      className={[groupEnd(index) ? "store-overview-boundary" : "", column.key === "store" ? "store-overview-label" : "", achievementTone(row, column.key)].filter(Boolean).join(" ")}>
      {cell(row, column)}
    </td>)}
  </tr>;
  return <div className="store-overview-view is-fit adviser-overview-table">
    <div className="store-overview-scroll" role="region" aria-label={props.ariaLabel} tabIndex={0} ref={windowed.scrollRef} onScroll={windowed.onScroll}>
      <table className="store-overview-table" style={{ minWidth: width }} aria-rowcount={sorted.length + 2}>
        <caption className="store-overview-sr-only">{props.ariaLabel}</caption>
        <colgroup>{columns.map(column => <col key={column.key} style={{ width: column.width / width * 100 + "%" }} />)}</colgroup>
        <thead><tr className="store-overview-headings">{columns.map((column, index) => {
          const reference = column.key === "achievement" && props.expectedTarget != null;
          return <th key={column.key} scope="col" data-column={column.key}
            aria-sort={sort?.key === column.key ? sort.direction === "asc" ? "ascending" : "descending" : "none"}
            aria-describedby={reference ? referenceId : undefined}
            className={[groupEnd(index) ? "store-overview-boundary" : "", column.key === "store" ? "store-overview-label" : "", reference ? "store-overview-reference-heading" : "", reference || column.key === "remaining" ? "store-overview-two-line-heading" : ""].filter(Boolean).join(" ")}>
            {sortButton(column)}
            {reference ? <span id={referenceId} className="store-overview-target-reference" role="note">{c[18]}{": %" + percent.format(props.expectedTarget!)}</span> : null}
          </th>;
        })}</tr></thead>
        <tbody>{renderRow(props.totalRow, true, 2)}<TableWindowSpacer height={windowed.before} columns={columns.length} />
          {sorted.slice(windowed.start, windowed.end).map((row, index) => renderRow(row, false, windowed.start + index + 3))}
          <TableWindowSpacer height={windowed.after} columns={columns.length} /></tbody>
      </table>
    </div>
  </div>;
}
